import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "./api";

export type ModelRoutingMode = "off" | "shadow" | "on";

export const modelRoutingModeResponseSchema = z.object({
  mode: z.enum(["off", "shadow", "on"]),
});

export async function fetchModelRoutingMode(): Promise<ModelRoutingMode> {
  const { mode } = await apiClient.get("/chat/model-routing-mode", modelRoutingModeResponseSchema);
  return mode;
}

export const modelRoutingModeQueryKey = ["model-routing-mode"] as const;

/** Reads `auto_chat_model_routing_mode` via the authenticated non-admin path.
 * Failures / missing data are treated as `off` by callers (fail-safe). */
export function useModelRoutingMode() {
  return useQuery({
    queryKey: modelRoutingModeQueryKey,
    queryFn: fetchModelRoutingMode,
    staleTime: 60_000,
  });
}

/** Composer Auto UX (label + hide effort) only when routing is active. */
export function isRoutingComposerActive(mode: ModelRoutingMode | undefined): boolean {
  return mode === "on" || mode === "shadow";
}

export function modelSelectorSentinelLabel(
  mode: ModelRoutingMode | undefined,
  defaultModelLabel: string | null,
): string {
  if (isRoutingComposerActive(mode)) return "Auto";
  return defaultModelLabel ?? "auto";
}

export function autoModelTooltip(
  mode: ModelRoutingMode | undefined,
  defaultModelLabel: string | null,
): string | undefined {
  if (!isRoutingComposerActive(mode)) return undefined;
  if (mode === "shadow") {
    return defaultModelLabel
      ? `Auto: usará ${defaultModelLabel} (routing en evaluación)`
      : "Auto: usará el modelo default (routing en evaluación)";
  }
  return "Auto: elige el modelo por complejidad (simple / standard / complex)";
}

export function showEffortSelector(
  mode: ModelRoutingMode | undefined,
  selectedModel: string,
  autoValue: string,
): boolean {
  return !isRoutingComposerActive(mode) || selectedModel !== autoValue;
}
